// src/pages/LeaderboardPage.js – Cinematic enhanced
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { leaderboardAPI } from '../utils/api';
import Chat from '../components/Chat';
import { useSound } from '../hooks/useSound';
import useViewport from '../hooks/useViewport';

const DEMO = [
  { username:'NeonCoder',  xp:2450, coins:890, level:5, avatar:'🤖', streak:12 },
  { username:'PixelWizard',xp:1800, coins:650, level:4, avatar:'🧙‍♂️',streak:7  },
  { username:'CyberNinja', xp:1200, coins:480, level:3, avatar:'🥷', streak:3  },
  { username:'DataDragon', xp:750,  coins:290, level:2, avatar:'🐉', streak:1  },
  { username:'StarCoder',  xp:500,  coins:200, level:2, avatar:'👾', streak:2  },
];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const { leaderboard: socketLb, onlineCount } = useSocket();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playHover } = useSound();
  const { isMobile } = useViewport();

  useEffect(() => {
    leaderboardAPI.getTop10()
      .then(r => setPlayers(r.data.leaderboard))
      .catch(() => {
        const all = [...DEMO];
        if (user) all.push({ username:user.username, xp:user.xp, coins:user.coins, level:user.level, avatar:user.avatar||'🧑‍💻', streak:user.streak||0 });
        setPlayers(all.sort((a,b) => b.xp - a.xp).slice(0,10));
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { if (socketLb?.length) setPlayers(socketLb); }, [socketLb]);

  const myRank = players.findIndex(p => p.username === user?.username);
  const medals = ['🥇','🥈','🥉'];

  return (
    <div className="page-shell" style={{ paddingBottom:'60px' }}>
      <div className="section-title animate-fade-in">GLOBAL RANKINGS</div>

      <div className="animate-fade-in" style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'18px', flexWrap:'wrap' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'7px',
                      background:'rgba(57,255,20,0.08)', border:'1px solid rgba(57,255,20,0.22)',
                      borderRadius:'20px', padding:'5px 13px', fontSize:'15px', fontWeight:700, color:'#39ff14',
                      boxShadow:'0 0 14px rgba(57,255,20,0.1)' }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#39ff14', animation:'pulse 2s infinite', boxShadow:'0 0 8px #39ff14' }} />
          {onlineCount} ONLINE
        </div>
        {myRank >= 0 && (
          <div style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:'rgba(255,255,255,0.4)', letterSpacing:'1px' }}>
            Your rank: <span style={{ color:'#00f5ff', textShadow:'0 0 8px rgba(0,245,255,0.4)' }}>#{myRank+1}</span>
          </div>
        )}
      </div>

      <div className="animate-fade-in mobile-scroll-x" style={{ background:'rgba(13,17,23,0.85)', border:'1px solid rgba(0,245,255,0.1)', borderRadius:'12px', overflow:'hidden', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 22px',
                      background:'rgba(0,245,255,0.05)', borderBottom:'1px solid rgba(0,245,255,0.09)',
                      fontFamily:'var(--font-game)', fontSize:'15px', color:'rgba(255,255,255,0.28)', letterSpacing:'2px', minWidth:isMobile ? '760px' : '0' }}>
          <span style={{ width:'38px' }}>#</span>
          <span style={{ width:'38px' }}></span>
          <span style={{ flex:1 }}>PLAYER</span>
          <span style={{ width:'80px', textAlign:'right' }}>LEVEL</span>
          <span style={{ width:'88px', textAlign:'right' }}>XP</span>
          <span style={{ width:'88px', textAlign:'right' }}>COINS</span>
          <span style={{ width:'65px', textAlign:'right' }}>STREAK</span>
        </div>

        {loading ? (
          <div style={{ padding:'40px', display:'flex', justifyContent:'center' }}><div className="loader"/></div>
        ) : players.map((p, i) => {
          const isMe = p.username === user?.username;
          return (
            <div key={`${p.username}-${i}`} style={{
              display:'flex', alignItems:'center', gap:'12px', padding:'15px 22px',
              borderBottom: i < players.length-1 ? '1px solid rgba(0,245,255,0.05)' : 'none',
              background: isMe ? 'rgba(0,245,255,0.05)' : 'transparent',
              transition:'all 0.22s cubic-bezier(0.16,1,0.3,1)',
              minWidth:isMobile ? '760px' : '0',
            }}
              onMouseEnter={e => { playHover(); e.currentTarget.style.background = isMe?'rgba(0,245,255,0.08)':'rgba(255,255,255,0.025)'; e.currentTarget.style.transform='translateX(3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isMe?'rgba(0,245,255,0.05)':'transparent'; e.currentTarget.style.transform=''; }}
            >
              <div style={{ width:'38px', textAlign:'center', fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700,
                            color: i===0?'#ffd700':i===1?'#c0c0c0':i===2?'#cd7f32':'rgba(255,255,255,0.3)',
                            textShadow: i<3 ? `0 0 10px currentColor` : 'none' }}>
                {i < 3 ? medals[i] : i+1}
              </div>
              <div style={{ width:'38px', fontSize:'20px', textAlign:'center' }}>{p.avatar||'🧑‍💻'}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:'15px', color: isMe?'#00f5ff':'#fff', display:'flex', alignItems:'center', gap:'7px' }}>
                  {p.username}
                  {isMe && <span style={{ fontSize:'15px', background:'rgba(0,245,255,0.15)', border:'1px solid rgba(0,245,255,0.3)', borderRadius:'10px', padding:'1px 7px', color:'#00f5ff', letterSpacing:'1px' }}>YOU</span>}
                </div>
              </div>
              <div style={{ width:'80px', textAlign:'right', fontFamily:'var(--font-game)', fontSize:'15px', color:'rgba(255,255,255,0.45)' }}>LVL {p.level}</div>
              <div style={{ width:'88px', textAlign:'right', fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, color:'#7c3aed' }}>{(p.xp||0).toLocaleString()}</div>
              <div style={{ width:'88px', textAlign:'right', fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, color:'#ffd700' }}>🪙{(p.coins||0).toLocaleString()}</div>
              <div style={{ width:'65px', textAlign:'right', fontSize:'15px', color:'#39ff14' }}>🔥{p.streak||0}</div>
            </div>
          );
        })}
      </div>
      <Chat />
    </div>
  );
}
